import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useToast } from "../../../services/utils";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  Tooltip,
} from "@mui/material";
import { useEffect, useState, useMemo, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { IconBtnEditBase, IconViewSvgWhite } from "../../../common/utils/icons";
import ViewPayloadDialog from "../../../components/Dialogs/ViewPayloadDialog";
import FlexBetween from "../../../components/FlexBetween";
import ResultStorageParameters from "../../../components/Rules/ResultStorageParameters";
import RunParameters from "../../../components/Rules/RunParameters";
import ScoringConfigOverrideAccordion from "../../../components/Scoring/ScoringConfigOverrideAccordion";
import { getValidationScore } from "../../../services/scoringService";
import { useResourceContext } from "../../../contexts/ResourceContext";
import useFetchFileProcessing from "../../../hooks/useFetchFileProcessing";
import { apiType, sqlDatabaseType } from "../../../services/constants";
import {
  backgroundReRunResourceValidation,
  ReRunResourceValidation,
  validateEngineRequest,
} from "../../../services/resourcesService";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import Loader from "../../../components/Molecules/Loader/Loader";
import useFetchAllConnectionKey from "../../../hooks/useFetchAllConnectionKey";
import FilterRules from "../../../components/Resource/FilterRules";
import InlineVariables from "../InlineVariables";
import CustomAccordian from "../../../components/Molecules/Accordian/CustomAccordion";
import useFetchResourceColumnsById from "../../../hooks/useFetchResourceColumnsById";
import AddMultipleGridList from "../../../components/AddMultipleGridList";
import { useBreadCrumbContext } from "../../../contexts/BreadCrumbContext";
import { useRuleResourceContext } from "../../../contexts/RuleResourceContext";
import { useSystemContext } from "../../../contexts/TagAndSystemProvider";
import useFetchAllSystem from "../../../hooks/useFetchAllSystem";
import { filterRulesNameSchema, filterRulesSqlSchema } from "../../../schemas";
import useFetchAllResourcesWithoutDomain from "../../../hooks/useFetchAllResourcesWithoutDomain";
import ReRunValidateResourceDataTab from "./ReRunValidateResourceDataTab";
import {
  IResultStorageParameters,
  IRunParameters,
} from "../../../types/Execution";
import { defaultResultStorageParameters } from "../../../services/models/execution.model";
import {
  getDefaultRunParameters,
  getQueryParams,
} from "../../../config/runParametersConfig";
import useFetchValidationRerunResultByExecutionId from "../../../hooks/useFetchValidationRerunResultByResourceId";
import useFetchResourcesByIdMultipleIds from "../../../hooks/useFetchResourcesByMultipleIds";
import { executionNameSchema } from "../../../schemas";
import { extractConnectionKeyIds } from "../../../utils/connectionKeyUtils";
import useFetchLinkedServices from "../../../hooks/useFetchLinkedServices";
import ExecutionName from "../../../components/Molecules/Rule/ExecutionName";
import { useAuth } from "../../../contexts/AuthContext";
import useFetchResourceColumnsByMultipleIds from "../../../hooks/useFetchResourceColumnsByMultipleIds";
import useFetchAllConnectionKeyWithoutId from "../../../hooks/useFetchAllConnectionKeyWithoutId";
import {
  parseReRunExecutionParams,
  resolveReRunExecutionSubGroup,
  sanitizeQueryParamValue,
} from "../../../utils/rerunQueryParams";

interface FilterFormData {
  filter_rules: any[];
}

const ReRunValidateResource = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const { showToast } = useToast();
  const { domainId, id } = useParams();
  const { isAdmin } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    setResourceColumnData,
    resourceColumnData,
    refetchData,
    errors,
    setErrors,
    setAllResourcesAdditionalData,
  } = useResourceContext();
  const {
    isResourceEdit,
    globalVariables,
    setGlobalVariables,
    setAllResourcesData,
    setFileProcessingData,
    setFetchedAllConnectionKeys,
    setResourceColumnsData,
  } = useRuleResourceContext();

  const [resourcesData, setResourcesData] = useState<any>({});
  const [aggregationBaseResourceData, setAggregationBaseResourceData] =
    useState<any>({});

  // REFRESH FIX: Track if initial data has been loaded to prevent overwriting saved changes
  const hasLoadedInitialData = useRef(false);
  const [isEditFilters, setIsEditFilters] = useState(false);
  const [allVariablesList, setAllVariablesList] = useState<any>({});
  const [filterFormData, setFilterFormData] = useState<any>(undefined);
  const [tempFilterFormData, setTempFilterFormData] = useState<any>({});
  const [hasFilterChanges, setHasFilterChanges] = useState<boolean>(false);
  const [intialFilterFormData, setIntialFilterFormData] =
    useState<any>(undefined);
  const [hasChanges, setHasChanges] = useState<boolean>(true);

  const [isBackdropLoading, setIsBackdropLoading] = useState<boolean>(false);
  const [resultStorageParameters, setResultStorageParameters] =
    useState<IResultStorageParameters>(defaultResultStorageParameters);
  const [runParameters, setRunParameters] = useState<IRunParameters>(
    getDefaultRunParameters("validateResource"),
  );
  const [scoreConfigOverride, setScoreConfigOverride] = useState<Record<
    string,
    any
  > | null>(null);
  const [priorScoreConfig, setPriorScoreConfig] = useState<Record<
    string,
    any
  > | null>(null);
  const [severityColumnNames, setSeverityColumnNames] = useState<any>({
    low: null,
    medium: null,
    high: null,
  });
  const [isEditResource, setIsEditResource] = useState(false);
  const [expandedAccordion, setExpandedAccordion] =
    useState<any>("resource-data");

  const [validationMessage, setValidationMessage] = useState<any[]>([]);
  const [checkValidation, setCheckValidation] = useState(false);
  const [currentResourceColumnId, setCurrentResourceColumnId] =
    useState<any>(null);

  const [validateResourcePayload, setValidateResourcePayload] =
    useState<any>(null);
  const [isEditVariables, setIsEditVariables] = useState(false);
  const [inlineVariables, setInlineVariables] = useState<any>({});
  const [tempInlineVariables, setTempInlineVariables] = useState<any>({});
  const [intialInlineVariables, setIntialInlineVariables] = useState<any>({});
  const [isReRunData, setIsReRunData] = useState<any>({
    validationID: null,
    isReRun: false,
    ...parseReRunExecutionParams(location.search),
  });
  const [executionName, setExecutionName] = useState("");
  const [executionMode, setExecutionMode] = useState(true);

  // Dialog state variables for View Payload functionality
  const [isPayloadDialogOpen, setIsPayloadDialogOpen] = useState(false);
  const [reRunValidationPayload, setReRunValidationPayload] =
    useState<any>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResponse, setValidationResponse] = useState<any>(null);

  const [fileProcessingData] = useFetchFileProcessing({ setIsLoading });
  const [linkedServices] = useFetchLinkedServices({ setIsLoading });
  // Always call hook but conditionally use the result
  const queryParams = new URLSearchParams(location.search);
  const selectedExecutionId = queryParams.get("validation-id");
  const [validationRerunResultData] =
    useFetchValidationRerunResultByExecutionId({
      currentExecutionId:
        selectedExecutionId && selectedExecutionId !== "null"
          ? Number(selectedExecutionId)
          : null,
      setIsLoading,
    });
  // Get the setter function from context (following linkedServicesData pattern)
  const { setAllSystemData } = useSystemContext();
  // Fetch system data directly in this component (following linkedServicesData pattern)
  const [systemData] = useFetchAllSystem({
    setIsLoading,
  });
  // Update context state when system data is fetched (following linkedServicesData pattern)
  useEffect(() => {
    if (systemData) {
      setAllSystemData(systemData);
    }
  }, [systemData, setAllSystemData]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reRun = params.get("re-run");
    const validationId = params.get("validation-id");
    if (reRun && validationId) {
      setIsReRunData({
        validationID: Number(validationId),
        isReRun: true,
        ...parseReRunExecutionParams(location.search),
      });
    } else {
      setIsReRunData({
        validationID: null,
        isReRun: false,
      });
    }
  }, [location]);

  useEffect(() => {
    setFileProcessingData(fileProcessingData);
  }, [fileProcessingData]);

  // Pre-load the scoring config snapshot from the prior validation execution
  // so the override accordion seeds its initial values from what was actually
  // used last time. A 404 (no snapshot) leaves priorScoreConfig as null and
  // the accordion will fall back to fetched defaults.
  useEffect(() => {
    if (!isReRunData?.validationID) return;
    let cancelled = false;
    getValidationScore(Number(isReRunData.validationID)).then((data) => {
      if (cancelled) return;
      const snapshot =
        data && typeof data === "object" && data.config_snapshot
          ? data.config_snapshot
          : null;
      setPriorScoreConfig(snapshot);
    });
    return () => {
      cancelled = true;
    };
  }, [isReRunData?.validationID]);

  const [resourceColumn] = useFetchResourceColumnsById({
    resourceColumnId: currentResourceColumnId,
    setIsLoading,
  });

  // FIXED: Use ref to prevent infinite loop by maintaining stable array reference
  const previousConnectionKeyIdsRef = useRef<number[]>([]);

  // Extract required connection key IDs from resource data and linked services
  const requiredConnectionKeyIds = useMemo(() => {
    if (!resourcesData) {
      return previousConnectionKeyIdsRef.current;
    }

    // Extract connection key IDs from resource data and linked services
    const ids = extractConnectionKeyIds(
      resourcesData,
      resourcesData?.additional_properties?.additional_resource_data,
      linkedServices,
      undefined, // outputStorageParams
      resourcesData?.additional_base_resource_data, // Pass additional base resources for ReRun data structure
    );

    // DIRECT FIX: Ensure all connection keys from resource definition are included
    const directConnectionKeyId =
      resourcesData?.additional_properties?.resource_definition?.connection_key;
    const apiConnectionKeyId =
      resourcesData?.additional_properties?.resource_definition?.api_definition
        ?.connection_key;

    // Also check for other possible connection key locations
    const sqlConnectionKeyId =
      resourcesData?.additional_properties?.resource_definition?.sql_definition
        ?.connection_key;
    const blobConnectionKeyId =
      resourcesData?.additional_properties?.resource_definition?.blob_definition
        ?.connection_key;
    const sftpConnectionKeyId =
      resourcesData?.additional_properties?.resource_definition?.sftp_definition
        ?.connection_key;
    const localConnectionKeyId =
      resourcesData?.additional_properties?.resource_definition
        ?.local_definition?.connection_key;

    if (directConnectionKeyId && !ids.includes(directConnectionKeyId)) {
      ids.push(directConnectionKeyId);
    }

    if (apiConnectionKeyId && !ids.includes(apiConnectionKeyId)) {
      ids.push(apiConnectionKeyId);
    }

    // DYNAMIC FIX: Force include any connection key IDs that are referenced in the resource
    const allReferencedConnectionKeys = [
      directConnectionKeyId,
      apiConnectionKeyId,
      sqlConnectionKeyId,
      blobConnectionKeyId,
      sftpConnectionKeyId,
      localConnectionKeyId,
      // Add any other connection key references we might find
    ].filter((id) => id && !ids.includes(id));

    allReferencedConnectionKeys.forEach((id) => {
      ids.push(id);
    });

    // CRITICAL FIX: Only return new array if IDs have actually changed
    const sortedIds = Array.from(new Set(ids)).sort((a, b) => a - b);
    const previousIds = previousConnectionKeyIdsRef.current;

    // Compare arrays to prevent infinite loops
    const idsChanged =
      sortedIds.length !== previousIds.length ||
      sortedIds.some((id, index) => id !== previousIds[index]);

    if (idsChanged) {
      previousConnectionKeyIdsRef.current = sortedIds;
      return sortedIds;
    } else {
      return previousConnectionKeyIdsRef.current;
    }
  }, [resourcesData, linkedServices]);

  // Fetch only the specific connection keys needed (optimized API call)
  const [fetchedConnectionKeys] = useFetchAllConnectionKeyWithoutId({
    setIsLoading,
  });

  // Extract resource column IDs from main resource and additional resources
  const resourceColumnIds = useMemo(() => {
    const colIds: number[] = [];
    // Add main resource column ID
    if (
      validationRerunResultData?.validation_request_body?.resource_data
        ?.resource_column_details_id
    ) {
      colIds.push(
        validationRerunResultData?.validation_request_body?.resource_data
          ?.resource_column_details_id,
      );
    }

    // Add additional resources column IDs
    if (
      validationRerunResultData?.validation_request_body?.resource_data
        ?.additional_base_resource_data?.length > 0
    ) {
      validationRerunResultData?.validation_request_body?.resource_data?.additional_base_resource_data.forEach(
        (res: any) => {
          const colId = res?.resource_column_details_id;
          if (colId && !colIds.includes(colId)) {
            colIds.push(colId);
          }
        },
      );
    }
    return colIds;
  }, [validationRerunResultData]);

  // Fetch resource columns for all resources (main + additional)
  const [resourceColumns] = useFetchResourceColumnsByMultipleIds({
    resourceColIds: resourceColumnIds,
    setIsLoading,
  });

  const [allResources] = useFetchAllResourcesWithoutDomain({
    setIsLoading,
    aggregation_type: "aggregated",
  });

  // Set resource columns data in context for column merging in LinkedServices
  useEffect(() => {
    if (resourceColumns?.length > 0) {
      setResourceColumnsData(resourceColumns);
    }
  }, [resourceColumns, setResourceColumnsData]);
  useEffect(() => {
    if (allResources) {
      setAllResourcesData(allResources);
      setAllResourcesAdditionalData(allResources);
    }
  }, [allResources, setAllResourcesData]);
  useEffect(() => {
    if (fetchedConnectionKeys) {
      setFetchedAllConnectionKeys(fetchedConnectionKeys);
    }
  }, [fetchedConnectionKeys, setFetchedAllConnectionKeys]);

  /**
   * Set run parametes when re run data update
   */
  useEffect(() => {
    // REFRESH FIX: Only load initial data once to prevent overwriting saved changes
    // When fetchedConnectionKeys changes (due to connection type change), this useEffect
    // would previously overwrite the locally updated resourcesData with stale validationRerunResultData
    if (
      validationRerunResultData &&
      fetchedConnectionKeys &&
      !hasLoadedInitialData.current
    ) {
      const severityColumnNames = {
        high:
          validationRerunResultData?.validation_request_body
            ?.severity_column_names?.["1"] || null,
        medium:
          validationRerunResultData?.validation_request_body
            ?.severity_column_names?.["2"] || null,
        low:
          validationRerunResultData?.validation_request_body
            ?.severity_column_names?.["3"] || null,
      };

      setSeverityColumnNames(severityColumnNames);

      const queryParams =
        validationRerunResultData?.validation_request_body?.query_params || {};
      const runInstance =
        validationRerunResultData?.validation_request_body?.run_instance || {};
      const executionSubGroup =
        validationRerunResultData?.validation_request_body
          ?.execution_sub_group || {};
      const severity =
        validationRerunResultData?.validation_request_body?.severity_level;

      // Start with defaults, override with values from previous execution
      const defaults = getDefaultRunParameters("validateResource");
      setRunParameters({
        ...defaults,
        // Override backend fields from previous execution's query params
        ...Object.fromEntries(
          Object.keys(queryParams)
            .filter((key) => key in defaults)
            .map((key) => [key, queryParams[key] ?? defaults[key]]),
        ),
        // Ensure impact_issues is false when store_all_data_columns is off
        impact_issues:
          (queryParams.store_all_data_columns ?? true)
            ? (queryParams.impact_issues ?? false)
            : false,

        // FE-only fields
        validation_severity_level:
          severity === 1 ? "high" : severity === 2 ? "medium" : "low",

        run_instance: {
          run_id: runInstance.run_id || 1,
          run_name: runInstance.run_name || "Legacy",
        },
        execution_sub_group: resolveReRunExecutionSubGroup(
          isReRunData,
          executionSubGroup,
        ),
        pull_latest_files:
          validationRerunResultData?.validation_request_body
            ?.pull_latest_files ?? false,
        sampling_validation:
          validationRerunResultData?.validation_request_body
            ?.sampling_validation ?? false,
        sample_size:
          validationRerunResultData?.validation_request_body?.sample_size ?? 0,
        isCustomiseSeverity:
          severityColumnNames.high ||
          severityColumnNames.medium ||
          severityColumnNames.low
            ? true
            : false,
      });

      setResultStorageParameters({
        ...defaultResultStorageParameters,
        linked_service_id:
          validationRerunResultData?.validation_request_body
            ?.output_storage_params?.output_storage_linked_service_id,
        linked_service_code:
          validationRerunResultData?.validation_request_body
            ?.output_storage_params?.output_storage_linked_service_code,
        connection_key_id:
          validationRerunResultData?.validation_request_body
            ?.output_storage_params?.output_storage_connection_key,
        file_path:
          validationRerunResultData?.validation_request_body
            ?.output_storage_params?.output_storage_base_file_path,
        validation_execution_report_name:
          validationRerunResultData?.validation_request_body
            ?.validation_execution_report_name,
      });

      const connectionKeyDetail = fetchedConnectionKeys?.find(
        (option: { id: any }) =>
          option.id ===
          validationRerunResultData?.validation_request_body?.resource_data
            ?.api_definition?.connection_key,
      );
      const resourceData =
        validationRerunResultData?.validation_request_body?.resource_data;
      const resourceType = resourceData?.resource_type;

      // Create a modified resource data object
      //aggregation_base_resource_data
      const additionalResource =
        resourceData?.additional_base_resource_data?.length > 0 &&
        resourceData?.additional_base_resource_data.map((item: any) => {
          return {
            ...item,
            type: item?.resource_type,
          };
        });
      let modifiedResourceData = {
        ...resourceData,
        id: resourceData?.resource_id,
        type: resourceData?.resource_type,
        api_definition: {
          ...resourceData?.api_definition,
          url: connectionKeyDetail?.api_url,
          request_timeout: 0,
        },
        additional_base_resource_data: additionalResource ?? [],
      };

      // If type is 'sftp', rename resource_path to remote_directory
      if (resourceType === "sftp" && resourceData?.resource_path) {
        modifiedResourceData = {
          ...modifiedResourceData,
          remote_directory: resourceData.resource_path,
        };
      }

      setResourcesData(modifiedResourceData);
      // ========================================
      // AGGREGATION BASE RESOURCE DATA SETUP
      // ========================================
      // Extract the base resource data from validation rerun result
      const baseResourceData =
        validationRerunResultData?.validation_request_body?.resource_data
          ?.aggregation_base_resource_data;
      // Transform the base resource data by adding the 'type' property
      // This ensures compatibility with the expected data structure
      const aggregationBaseResourceData = {
        ...baseResourceData,
        type: baseResourceData?.resource_type,
      };
      // Set the transformed aggregation base resource data to state
      setAggregationBaseResourceData(aggregationBaseResourceData);
      setInlineVariables(
        validationRerunResultData?.validation_request_body?.inline_variables,
      );
      setTempInlineVariables(
        validationRerunResultData?.validation_request_body?.inline_variables,
      );
      if (validationRerunResultData?.validation_request_body?.inline_variables)
        setIntialInlineVariables(
          validationRerunResultData?.validation_request_body?.inline_variables,
        );
      const filter_rules = validationRerunResultData?.validation_request_body
        ?.resource_data?.filter_rules
        ? validationRerunResultData?.validation_request_body?.resource_data?.filter_rules.map(
            (rule: any) => ({
              ...rule,
              id: Math.random(),
            }),
          )
        : [];

      setFilterFormData({
        filter_rules: filter_rules,
        additional_resource_data:
          validationRerunResultData?.validation_request_body?.resource_data
            ?.additional_base_resource_data,
      });
      setTempFilterFormData({
        filter_rules: filter_rules,
        additional_resource_data:
          validationRerunResultData?.validation_request_body?.resource_data
            ?.additional_base_resource_data,
      });
      setIntialFilterFormData({
        filter_rules: filter_rules,
        additional_resource_data:
          validationRerunResultData?.validation_request_body?.resource_data
            ?.additional_base_resource_data,
      });
      setGlobalVariables({
        ...globalVariables,
        ...validationRerunResultData?.validation_request_body?.inline_variables,
      });

      setCurrentResourceColumnId(
        validationRerunResultData?.validation_request_body?.resource_data
          ?.resource_column_details_id,
      );
      setExecutionName(
        validationRerunResultData?.validation_request_body?.execution_name ||
          "",
      );
      setCurrentBreadcrumbPage({
        name: validationRerunResultData?.validation_request_body?.resource_data
          ?.resource_name,
        id: validationRerunResultData?.validation_request_body?.resource_data
          ?.resource_id,
      });
      // Mark that initial data has been loaded
      hasLoadedInitialData.current = true;
      return () => {
        setCurrentBreadcrumbPage({ name: "", id: null });
      };
    }
  }, [validationRerunResultData, fetchedConnectionKeys]);

  useEffect(() => {
    const formChanged =
      JSON.stringify(tempFilterFormData) !==
      JSON.stringify(intialFilterFormData);
    setHasFilterChanges(formChanged);
  }, [tempFilterFormData, intialFilterFormData]);

  useEffect(() => {
    if (resourceColumn && Object.keys(resourceColumn)?.length) {
      setResourceColumnData(resourceColumn);
    }
  }, [resourceColumn]);

  const getFilePreProcessing = (id: string | number | undefined) => {
    const filePreProcessing = fileProcessingData?.find(
      (file: { id: string | number | null | undefined }) => file.id === id,
    );
    return filePreProcessing
      ? filePreProcessing.file_processing_attributes
      : null;
  };

  const aggregationBaseResource = (resourceData: any) => {
    const resource: any = resourceData;
    const type = resource?.type || resource?.resource_type;
    const {
      user_name,
      password,
      bearer_token,
      api_key,
      oauth_client_id,
      oauth_client_secret,
      oauth_url,
      method,
      content_type,
      body,
      query_params,
      url_params,
      url,
      request_timeout,
    } = getApiDefinition(resource);
    const additional_base_resource_data =
      resource?.additional_base_resource_data
        ? processAdditionalResourceData(resource?.additional_base_resource_data)
        : null;
    let resourceObject: any = {
      aggregation_type: resource?.aggregation_type,
      resource_name: resource?.resource_name,
      resource_column_details_id: resource?.resource_column_details_id,
      file_processing_id: resource?.file_processing_id,
      resource_id: resource?.resource_id || null,
      linked_service_id: resource?.linked_service_id || null,
      linked_service_code: resource?.linked_service_code || null,
      connection_key: resource?.connection_key,
      sql_query: resource?.sql_query,
      container_name: resource?.container_name,
      resource_path:
        resource?.resource_type === "sftp"
          ? resource?.remote_directory
          : resource?.resource_path,
      file_name: resource?.file_name,
      column_delimiter: resource?.column_delimiter,
      use_multi_thread_reader: resource?.use_multi_thread_reader,
      column_name_to_partition_on_sql_query:
        resource?.column_name_to_partition_on_sql_query,
      use_parquet_export: resource?.use_parquet_export || false,
      parquet_export_definition: resource?.use_parquet_export
        ? resource?.parquet_export_definition || null
        : null,
      excel_sheet_name: resourcesData?.excel_sheet_name,
      additional_base_resource_data,
      file_pre_processing:
        sqlDatabaseType.includes(type) || apiType.includes(type)
          ? null
          : getFilePreProcessing(resource?.file_processing_id),
      aggregation_properties:
        resource?.aggregation_type === "flat"
          ? null
          : resource?.additional_properties?.aggregation_properties || null,
      aggregation_base_resource_data: null,
      api_request: {
        user_name,
        password,
        bearer_token,
        api_key,
        oauth_client_id,
        oauth_client_secret,
        oauth_url,
        method,
        content_type,
        body,
        query_params,
        url_params,
        url,
        request_timeout,
      },
    };
    checkRequiredValidation(resource, resourceObject);

    resourceObject = Object.fromEntries(
      Object.entries(resourceObject).filter(([_, v]) => v !== null),
    );
    if (
      Object.keys(resourceObject).length === 0 &&
      resourceObject.constructor === Object
    ) {
      return null;
    }
    return Object.keys(resourceObject).length !== 0 ? resourceObject : null;
  };

  const checkOutputStorageParamsNull = (
    output_storage_params: { [s: string]: unknown } | ArrayLike<unknown>,
  ) => {
    return Object.values(output_storage_params).some((value) => value === null);
  };
  const getApiDefinition = (resource: any) => {
    let user_name = resource?.api_definition?.user_name || null;
    let password = resource?.api_definition?.password || null;
    let bearer_token = resource?.api_definition?.bearer_token || null;
    let api_key = resource?.api_definition?.api_key || null;
    let oauth_client_id = resource?.api_definition?.oauth_client_id || null;
    let oauth_client_secret =
      resource?.api_definition?.oauth_client_secret || null;
    let oauth_url = resource?.api_definition?.oauth_url || null;
    let method = resource?.api_definition?.method || "get";
    let content_type =
      resource?.api_definition?.content_type || "application/json";
    let body = resource?.api_definition?.body || null;
    let query_params = resource?.api_definition?.query_params || null;
    let url_params = resource?.api_definition?.url_params || null;
    let url = resource?.api_definition?.url || null;
    let request_timeout = resource?.api_definition?.request_timeout || 0;
    return {
      user_name,
      password,
      bearer_token,
      api_key,
      oauth_client_id,
      oauth_client_secret,
      oauth_url,
      method,
      content_type,
      body,
      query_params,
      url_params,
      url,
      request_timeout,
    };
  };

  const processAdditionalResourceData = (additionalResourceData: any) => {
    if (!Array.isArray(additionalResourceData)) {
      return null;
    }
    const res = additionalResourceData
      .map((resource) => aggregationBaseResource(resource))
      .filter(Boolean);
    return res;
  };

  // Pure function to build re-run validation payload
  const buildReRunValidationPayload = () => {
    // Build output_storage_params
    let output_storage_params = {
      output_storage_linked_service_id:
        resultStorageParameters?.linked_service_id || null,
      output_storage_linked_service_code:
        resultStorageParameters?.linked_service_code || null,
      output_storage_connection_key:
        resultStorageParameters?.connection_key_id || null,
      output_storage_base_file_path: resultStorageParameters?.file_path || null,
    };

    const isOutputStorageParamsNull = checkOutputStorageParamsNull(
      output_storage_params,
    );

    // Build run_instance
    const run_instance = {
      run_id: runParameters?.run_instance?.run_id || 1,
      run_name: runParameters?.run_instance?.run_name || "Legacy",
    };

    // Build execution_sub_group
    const execution_sub_group = {
      execution_sub_group_id:
        runParameters?.execution_sub_group?.execution_sub_group_id || 0,
      execution_sub_group_code:
        runParameters?.execution_sub_group?.execution_sub_group_code || "",
    };

    // Calculate severity level
    const severity_level =
      runParameters?.validation_severity_level === "high"
        ? 1
        : runParameters?.validation_severity_level === "medium"
          ? 2
          : 3;

    // Get validation_execution_report_name
    const validation_execution_report_name =
      resultStorageParameters?.validation_execution_report_name || null;

    // Build severity_column_names
    let severityColumnNamesPayload = {
      1: severityColumnNames.high,
      2: severityColumnNames.medium,
      3: severityColumnNames.low,
    };

    const severity_column_names: any = Object.fromEntries(
      Object.entries(severityColumnNamesPayload).filter(([_, v]) => v !== null),
    );

    // Build resource_data
    const type = resourcesData?.type || resourcesData?.resource_type;

    const {
      user_name,
      password,
      bearer_token,
      api_key,
      oauth_client_id,
      oauth_client_secret,
      oauth_url,
      method,
      content_type,
      body,
      query_params,
      url_params,
      url,
      request_timeout,
    } = getApiDefinition(resourcesData);

    const additional_base_resource_data =
      resourcesData?.additional_base_resource_data
        ? processAdditionalResourceData(
            resourcesData?.additional_base_resource_data,
          )
        : null;

    let resourceObject: any = {
      aggregation_type: resourcesData?.aggregation_type,
      resource_name: resourcesData?.resource_name,
      resource_column_details_id: resourcesData?.resource_column_details_id,
      file_processing_id: resourcesData?.file_processing_id,
      resource_id: resourcesData?.resource_id || null,
      linked_service_id: resourcesData?.linked_service_id || null,
      linked_service_code: resourcesData?.linked_service_code || null,
      connection_key: resourcesData?.connection_key,
      sql_query: resourcesData?.sql_query,
      container_name: resourcesData?.container_name,
      resource_path:
        resourcesData?.resource_type === "sftp"
          ? resourcesData?.remote_directory
          : resourcesData?.resource_path,
      file_name: resourcesData?.file_name,
      column_delimiter: resourcesData?.column_delimiter,
      use_multi_thread_reader: resourcesData?.use_multi_thread_reader,
      column_name_to_partition_on_sql_query:
        resourcesData?.column_name_to_partition_on_sql_query,
      use_parquet_export: resourcesData?.use_parquet_export || false,
      parquet_export_definition: resourcesData?.use_parquet_export
        ? resourcesData?.parquet_export_definition || null
        : null,
      excel_sheet_name: resourcesData?.excel_sheet_name,
      additional_base_resource_data,
      file_pre_processing:
        sqlDatabaseType.includes(type) || apiType.includes(type)
          ? null
          : getFilePreProcessing(resourcesData?.file_processing_id),
      filter_rules: filterFormData?.filter_rules ?? [],
      aggregation_properties:
        resourcesData?.aggregation_type === "flat"
          ? null
          : resourcesData?.aggregation_properties || null,
      aggregation_base_resource_data:
        resourcesData?.aggregation_type === "flat"
          ? null
          : aggregationBaseResource(aggregationBaseResourceData) || null,
      api_request: {
        user_name,
        password,
        bearer_token,
        api_key,
        oauth_client_id,
        oauth_client_secret,
        oauth_url,
        method,
        content_type,
        body,
        query_params,
        url_params,
        url,
        request_timeout,
      },
    };

    checkRequiredValidation(resourcesData, resourceObject);

    resourceObject = Object.fromEntries(
      Object.entries(resourceObject).filter(([_, v]) => v !== null),
    );

    // Build base payload with only backend-expected query params
    const filteredQueryParams = getQueryParams(
      "validateResource",
      runParameters,
    );
    // Override with values from previous execution data
    filteredQueryParams.no_of_sample_validation_errors =
      validationRerunResultData?.validation_request_body?.query_params
        ?.no_of_sample_validation_errors ??
      filteredQueryParams.no_of_sample_validation_errors;
    filteredQueryParams.no_of_errors_in_filter_record_output_files =
      validationRerunResultData?.validation_request_body?.query_params
        ?.no_of_errors_in_filter_record_output_files ??
      filteredQueryParams.no_of_errors_in_filter_record_output_files;

    let reqBody: any = {
      validation_request_body: {
        execution_name: executionName,
        resource_data: resourceObject,
        pull_latest_files: runParameters?.pull_latest_files ?? false,
        sampling_validation: runParameters?.sampling_validation ?? false,
        sample_size: runParameters?.sampling_validation
          ? parseInt(String(runParameters?.sample_size)) || 0
          : 0,
        query_params: filteredQueryParams,
        severity_level: severity_level,
        run_instance: run_instance,
        execution_sub_group: execution_sub_group,
      },
      resource_id: validationRerunResultData?.resource_id,
      resource_column_details_version:
        validationRerunResultData?.resource_column_details_version,
      resource_version: validationRerunResultData?.resource_version,
      domain_version: validationRerunResultData?.domain_version,
      file_names: validationRerunResultData?.file_names,
    };

    // Include output_storage_params if not null
    if (!isOutputStorageParamsNull) {
      reqBody.validation_request_body.output_storage_params =
        output_storage_params;
    }

    // Include validation_execution_report_name if not null
    if (validation_execution_report_name !== null) {
      reqBody.validation_request_body.validation_execution_report_name =
        validation_execution_report_name;
    }

    // Include severity_column_names if configured
    if (Object.keys(severity_column_names).length > 0) {
      reqBody.validation_request_body.severity_column_names =
        severity_column_names;
    }

    // Include inline_variables if present
    if (globalVariables && Object.keys(globalVariables).length > 0) {
      reqBody.validation_request_body.inline_variables = globalVariables;
    }

    // Attach scoring overrides only when scoring is enabled and the user has
    // actually modified values in the override accordion. Otherwise the field
    // is omitted so the backend uses the prior config_snapshot / defaults.
    if (runParameters?.generate_score && scoreConfigOverride) {
      reqBody.validation_request_body.score_config_params = scoreConfigOverride;
    }

    return reqBody;
  };

  // Handle validating payload via the engine validate-request endpoint
  const handleValidatePayload = async (payload: any) => {
    setIsValidating(true);
    try {
      const validateReqBody = {
        target_endpoint: "/validations/rerun",
        method: "POST",
        body: payload,
        query_params: {},
        url_params: {},
      };
      const response = await validateEngineRequest(validateReqBody);
      setValidationResponse(response);
      if (response?.is_valid) {
        showToast("Payload is valid!", "success");
      }
    } catch (error: any) {
      showToast(error?.response?.data?.message || "Validation failed", "error");
    } finally {
      setIsValidating(false);
    }
  };

  // Handle copying payload to clipboard
  const handleCopyPayload = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      const payload = buildReRunValidationPayload();
      const formattedPayload = JSON.stringify(payload, null, 2);
      await navigator.clipboard.writeText(formattedPayload);
      showToast("JSON copied to clipboard!", "success");
    } catch (err) {
      console.error("Failed to copy JSON:", err);
      showToast("Failed to copy JSON to clipboard", "error");
    }
  };

  useEffect(() => {
    const validateResourceData = () => {
      setValidationMessage([]);

      // Validate inline variables using globalVariables (current state)
      // This ensures validation works even when inline_variables is not in the differential payload
      if (globalVariables && Object.keys(globalVariables).length > 0) {
        Object.keys(globalVariables).forEach((key) => {
          if (globalVariables[key] === "") {
            setValidationMessage((prev) => [
              ...prev,
              {
                id: `Resource Variables`,
                message: `Please enter value for ${key}`,
              },
            ]);
          }
        });
      }

      // Build the payload after validation using the extracted function
      const reqBody = buildReRunValidationPayload();
      setValidateResourcePayload(reqBody);
    };
    if (checkValidation) validateResourceData();
  }, [checkValidation]);

  useEffect(() => {
    if (validationMessage.length === 0 && validateResourcePayload) {
      setIsBackdropLoading(true);
      if (executionMode) {
        backgroundReRunResourceValidation(validateResourcePayload)
          .then(() => {
            showToast(
              "Your re-run resource validation is in the queue. We will notify you once it's done!",
              "warn",
            );
            navigate(`/validation-execution-history`);
          })
          .catch((error) => {
            console.error(error?.response?.data?.message);
          })
          .finally(() => {
            setCheckValidation(false);
            setValidateResourcePayload(null);
            setIsBackdropLoading(false);
          });
      } else {
        ReRunResourceValidation(validateResourcePayload)
          .then((response) => {
            if (response) {
              showToast("Resource re-validated successfully!", "success");
              navigate(
                `/validation-execution-history/validate-result/${response?.id}`,
                {
                  state: {
                    response,
                    isValidateResourceResponse: true,
                  },
                },
              );
            }
          })
          .catch((error) => {
            console.error(error?.response?.data?.message);
          })
          .finally(() => {
            setCheckValidation(false);
            setValidateResourcePayload(null);
            setIsBackdropLoading(false);
          });
      }
    } else if (validationMessage.length > 0) {
      const temp = (
        <div>
          Not all fields are configured. Please check the following fields:
          {validationMessage.map((msg) => (
            <div>
              <span style={{ fontWeight: 900 }}>{msg.id}</span>-{msg.message}
            </div>
          ))}
        </div>
      );
      showToast(temp, "warning");
      setCheckValidation(false);
      return;
    }
  }, [validationMessage, validateResourcePayload]);
  const checkRequiredValidation = (resource: any, resourceObject: any) => {
    const type = resource?.type || resource?.resource_type;
    const fieldNamesMap: { [key: string]: string } = {
      connection_key: "Connection Key",
      container_name: "Container Name",
      resource_path: "Resource Path",
      file_name: "File Name",
      column_delimiter: "Column Delimiter",
      base_resource_id: "Base resource",
      base_resource_columns: "Base Resource Columns",
      aggregation_query: "Aggregation Query",
      sql_query: "Sql Query",
      user_name: "Username",
      password: "Password",
      url: "Url",
      request_timeout: "Request Timeout",
      bearer_token: "Bearer Token",
      api_key: "Api Key",
      oauth_client_id: "Oauth Client Id",
      oauth_client_secret: "Oauth Client Secret",
      oauth_url: "Oauth Url",
    };
    if (resource?.aggregation_type === "flat") {
      let requiredFields: any = [];
      if (sqlDatabaseType.includes(type)) {
        requiredFields = ["connection_key", "sql_query"];
      } else if (type === "sftp" || type === "local") {
        requiredFields = [
          "connection_key",
          "resource_path",
          "file_name",
          "column_delimiter",
        ];
      } else if (type === "blob") {
        requiredFields = [
          "connection_key",
          "resource_path",
          "file_name",
          "column_delimiter",
          "container_name",
        ];
      } else if (type === "basic_auth_api") {
        requiredFields = [
          "connection_key",
          "user_name",
          "password",
          "url",
          "request_timeout",
        ];
      } else if (type === "token_auth_api") {
        requiredFields = [
          "connection_key",
          "bearer_token",
          "url",
          "request_timeout",
        ];
      } else if (type === "key_auth_api") {
        requiredFields = [
          "connection_key",
          "api_key",
          "url",
          "request_timeout",
        ];
      } else if (type === "oauth_api") {
        requiredFields = [
          "connection_key",
          "oauth_client_id",
          "oauth_client_secret",
          "oauth_url",
          "url",
          "request_timeout",
        ];
      }
      let missingFields: any;
      if (
        type === "basic_auth_api" ||
        type === "token_auth_api" ||
        type === "key_auth_api" ||
        type === "oauth_api"
      ) {
        missingFields = requiredFields?.filter(
          (field: any) => resourceObject?.api_request[field] === null,
        );
      } else {
        missingFields =
          requiredFields &&
          requiredFields.filter((field: any) => resourceObject[field] === null);
      }

      if (missingFields && missingFields.length > 0) {
        const missingFieldNames = missingFields.map(
          (field: any) => `${fieldNamesMap[field]}`,
        );
        setValidationMessage((prev) => [
          ...prev,
          {
            id: resource?.resource_name,
            message: missingFieldNames.join(", "),
          },
        ]);
        // return false;
      }
    } else if (
      resource?.aggregation_type === "aggregated" &&
      resourceObject.aggregation_properties !== null
    ) {
      const requiredFields: string[] = [
        "base_resource_id",
        "base_resource_columns",
        "aggregation_query",
      ];
      const missingFields = requiredFields.filter(
        (field) => resourceObject.aggregation_properties[field] === null,
      );

      if (missingFields.length > 0) {
        const missingFieldNames = missingFields.map(
          (field) => `${fieldNamesMap[field]}`,
        );
        setValidationMessage((prev) => [
          ...prev,
          {
            id: resource?.resource_name,
            message: missingFieldNames.join(", "),
          },
        ]);
      }
    }
  };
  const handleChangeAccordion =
    (panel: string) => (_: any, isExpanded: any) => {
      if (isResourceEdit !== "") {
        showToast("Please save changes first!!", "warning");
        return;
      }
      setExpandedAccordion(isExpanded ? panel : null);
    };

  const handleViewResource = (domainId: any, resource: any, e: any) => {
    e.preventDefault();
    if (isResourceEdit !== "") {
      return;
    }
    navigate(`/resource/${domainId}/view/${resource}`);
  };
  const handleChangeVariable = (event: any) => {
    const { name, value } = event.target;
    setTempInlineVariables((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    const formChanged =
      JSON.stringify(tempInlineVariables) !==
      JSON.stringify(intialInlineVariables);
    setHasChanges(formChanged);
  }, [tempInlineVariables, intialInlineVariables]);

  useEffect(() => {
    refetchData();
  }, [id]);
  const checkFilterRuleValidation = async (
    updatedFormData: any,
    schemaType: any,
  ) => {
    const getSchemaType =
      schemaType === "name" ? filterRulesNameSchema : filterRulesSqlSchema;
    for (let index = 0; index < updatedFormData.filter_rules.length; index++) {
      const element = updatedFormData.filter_rules[index];
      try {
        await getSchemaType.validate(element, { abortEarly: false });
        const updatedJsonData = { ...errors };
        const updateKey = schemaType === "name" ? "name" : "sql_query";
        if (updatedJsonData.filter_rules.length > index) {
          updatedJsonData.filter_rules[index][updateKey] = undefined;
        } else {
          updatedJsonData.filter_rules.push({ [updateKey]: undefined });
        }
        setErrors(updatedJsonData);
      } catch (validationErrors: any) {
        const updatedJsonData = { ...errors };
        const updateKey = schemaType === "name" ? "name" : "sql_query";
        if (updatedJsonData.filter_rules.length > index) {
          updatedJsonData.filter_rules[index][updateKey] =
            validationErrors.message;
        } else {
          updatedJsonData.filter_rules.push({
            [updateKey]: validationErrors.message,
          });
        }
        setErrors(updatedJsonData);
      }
    }
  };
  const handleExecuteSubmit = async () => {
    try {
      await executionNameSchema.validate(
        { execution_name: executionName },
        {
          abortEarly: false,
        },
      );
      setCheckValidation(true);
    } catch (validationErrors: any) {
      const newErrors: { [key: string]: string } = {};
      validationErrors.inner.forEach(
        (error: { path: string | number; message: string }) => {
          newErrors[error.path] = error.message;
        },
      );
      setErrors((prevErrors: any) => ({
        ...prevErrors,
        ...newErrors,
      }));
    }
  };

  return (
    <>
      <Loader isLoading={isBackdropLoading} />
      <Loader isLoading={isLoading} />
      <div
        className="accordion-panel"
        style={{ marginBottom: "0", marginTop: "12px" }}
      >
        <Box>
          <ExecutionName
            handleChangeAccordion={handleChangeAccordion}
            errors={errors}
            setErrors={setErrors}
            executionName={executionName}
            setExecutionName={setExecutionName}
            executionMode={executionMode}
            setExecutionMode={setExecutionMode}
            runParameters={runParameters}
            setRunParameters={setRunParameters}
            setIsLoading={setIsLoading}
            isReRun={true}
          />
          <Accordion
            className="heading-bold box-shadow color-palette color-palette-2"
            expanded={expandedAccordion === "resource-data"}
            onChange={handleChangeAccordion("resource-data")}
          >
            <AccordionSummary
              aria-controls="panel1d-content"
              id="panel1d-header"
              expandIcon={<ExpandMoreIcon />}
            >
              <div className="custom-actions">
                <div className="heading">Resource Data</div>
                <div className="action-btns">
                  <Tooltip title="Navigate to view resource" placement="top">
                    <button
                      className="view-icon"
                      onClick={(e) => handleViewResource(domainId, id, e)}
                    >
                      <IconViewSvgWhite />
                    </button>
                  </Tooltip>
                </div>
              </div>
            </AccordionSummary>
            <AccordionDetails sx={{ paddingTop: "6px" }}>
              <ReRunValidateResourceDataTab
                resourcesData={resourcesData}
                isLoading={isBackdropLoading}
                setResourcesData={setResourcesData}
                setIsLoading={setIsLoading}
                fileProcessingData={fileProcessingData}
                aggregationBaseResourceData={aggregationBaseResourceData}
                setAggregationBaseResourceData={setAggregationBaseResourceData}
                connectionKeysData={fetchedConnectionKeys}
              />
            </AccordionDetails>
          </Accordion>

          <RunParameters
            runParameters={runParameters}
            setRunParameters={setRunParameters}
            isEditResource={isEditResource}
            setIsEditResource={setIsEditResource}
            setIsLoading={setIsLoading}
            severityColumnNames={severityColumnNames}
            setSeverityColumnNames={setSeverityColumnNames}
            expandedAccordion={expandedAccordion}
            handleChangeAccordion={handleChangeAccordion}
            feature="validateResource"
            isReRun={isReRunData?.isReRun}
          />

          <ScoringConfigOverrideAccordion
            type="validation"
            generateScore={!!runParameters?.generate_score}
            currentConfig={priorScoreConfig}
            expanded={expandedAccordion === "scoring-config-overrides"}
            onChange={handleChangeAccordion("scoring-config-overrides")}
            onOverrideChange={setScoreConfigOverride}
            disabled={isEditResource}
          />

          <Accordion
            className="mt-8 heading-bold box-shadow color-palette color-palette-1"
            expanded={expandedAccordion === "result-storage-parameters"}
            onChange={handleChangeAccordion("result-storage-parameters")}
          >
            <AccordionSummary
              aria-controls="panel1d-content"
              id="panel1d-header"
              expandIcon={<ExpandMoreIcon />}
            >
              Result Storage Parameters
            </AccordionSummary>
            <AccordionDetails sx={{ paddingTop: "16px" }}>
              <ResultStorageParameters
                resultStorageParameters={resultStorageParameters}
                setResultStorageParameters={setResultStorageParameters}
                isEditResource={isEditResource}
                setIsEditResource={setIsEditResource}
                setIsLoading={setIsLoading}
                isFromRule={false}
              />
            </AccordionDetails>
          </Accordion>

          {inlineVariables && Object.keys(inlineVariables).length > 0 && (
            <Accordion
              className="mt-8 heading-bold box-shadow color-palette color-palette-1"
              expanded={expandedAccordion === "inline-variables"}
              onChange={handleChangeAccordion("inline-variables")}
            >
              <AccordionSummary
                aria-controls="panel1d-content"
                id="panel1d-header"
                expandIcon={<ExpandMoreIcon />}
              >
                Resource Variables
              </AccordionSummary>
              <AccordionDetails sx={{ paddingTop: "16px" }}>
                <div className="text-box-card box-card-variables full-radius">
                  {!isEditVariables ? (
                    <div className="inline-variables-parent">
                      <InlineVariables
                        inlineVariables={inlineVariables || []}
                        handleChangeVariable={handleChangeVariable}
                        isReadOnly={true}
                      />
                    </div>
                  ) : (
                    <div className="inline-variables-parent">
                      <InlineVariables
                        inlineVariables={tempInlineVariables || []}
                        handleChangeVariable={handleChangeVariable}
                        isReadOnly={false}
                      />
                    </div>
                  )}
                  {!isEditVariables ? (
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      md
                      sx={{
                        justifyContent: "flex-end",
                        display: "flex",
                        marginTop: "8px",
                      }}
                    >
                      <button
                        className="btn-nostyle icon-btn-edit"
                        onClick={() => {
                          setIsEditVariables(true);
                          setTempInlineVariables(inlineVariables);
                        }}
                      >
                        <IconBtnEditBase ariaLabel="Edit variables" />
                      </button>
                    </Grid>
                  ) : (
                    <Grid
                      item
                      xs
                      sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          columnGap: "8px",
                          flexWrap: "wrap",
                          marginTop: "8px",
                        }}
                      >
                        <Button
                          color="secondary"
                          variant="contained"
                          onClick={() => {
                            setIsEditVariables(false);
                          }}
                          className="btn-orange btn-dark"
                        >
                          Cancel
                        </Button>
                        <Button
                          color="secondary"
                          variant="contained"
                          onClick={() => {
                            setInlineVariables(tempInlineVariables);
                            setGlobalVariables((prev: any) => ({
                              ...prev,
                              ...tempInlineVariables,
                            }));
                            setIsEditVariables(false);
                          }}
                          className="btn-orange"
                          disabled={!hasChanges}
                        >
                          <SaveOutlinedIcon /> &nbsp; Save
                        </Button>
                      </Box>
                    </Grid>
                  )}
                </div>
              </AccordionDetails>
            </Accordion>
          )}

          {filterFormData?.filter_rules &&
            filterFormData?.filter_rules.length > 0 && (
              <CustomAccordian
                expandId="filter-rules"
                title="Filter Rules"
                handleChangeAccordion={handleChangeAccordion}
                isEnabled={true}
                className="color-palette color-palette-1"
              >
                <FilterRules
                  formData={
                    !isEditFilters ? filterFormData : tempFilterFormData
                  }
                  isAddButtonHidden={true}
                  setFormData={setTempFilterFormData}
                  isViewOnly={!isEditFilters}
                  resourceColumnData={resourceColumnData}
                  checkFilterRuleValidation={checkFilterRuleValidation}
                  setAllVariablesList={setAllVariablesList}
                />

                <Grid
                  item
                  xs={12}
                  sm={12}
                  md
                  sx={{
                    justifyContent: "flex-end",
                    display: "flex",
                    marginTop: "8px",
                  }}
                >
                  {!isEditFilters && (
                    <button
                      className="btn-nostyle icon-btn-edit"
                      onClick={() => {
                        setIsEditFilters((prev) => !prev);
                      }}
                    >
                      <IconBtnEditBase ariaLabel="Edit filter rules" />
                    </button>
                  )}

                  {isEditFilters && (
                    <Grid
                      item
                      xs
                      sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          columnGap: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        <Button
                          color="secondary"
                          variant="contained"
                          onClick={() => {
                            setIsEditFilters((prev: any) => !prev);
                            setTempFilterFormData(filterFormData);
                          }}
                          className="btn-orange btn-dark"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          onClick={() => {
                            setIsEditFilters((prev) => !prev);
                            setFilterFormData(tempFilterFormData);
                            setIntialFilterFormData(tempFilterFormData);
                          }}
                          variant="contained"
                          color="secondary"
                          className="btn-orange"
                          title="Save Resource"
                          disabled={!hasFilterChanges}
                        >
                          <SaveOutlinedIcon /> &nbsp; Save
                        </Button>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CustomAccordian>
            )}
          {resourceColumnData?.resource_column_properties
            ?.cross_field_validations &&
            resourceColumnData?.resource_column_properties
              ?.cross_field_validations.length > 0 && (
              <CustomAccordian
                expandId="adhoc-query"
                title="Adhoc Query"
                handleChangeAccordion={handleChangeAccordion}
                isEnabled={true}
                className="color-palette color-palette-1"
              >
                <AddMultipleGridList
                  gridValue={
                    resourceColumnData?.resource_column_properties
                      ?.cross_field_validations
                  }
                  setGridValue={() => {}}
                  isViewOnly={true}
                  buttonName=" "
                  errors={{}}
                  setErrors={() => {}}
                />
              </CustomAccordian>
            )}
          {!isEditResource && isResourceEdit === "" && (
            <FlexBetween
              gap="3rem"
              sx={{
                marginTop: 2,
                display: "flex",
                justifyContent: "flex-end",
                gap: "1rem",
              }}
            >
              {isAdmin && (
                <Button
                  className="btn-orange btn-dark"
                  onClick={() => {
                    const payload = buildReRunValidationPayload();
                    setReRunValidationPayload(payload);
                    setValidationResponse(null);
                    setIsPayloadDialogOpen(true);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0,
                    padding: "6px 16px",
                    minWidth: "auto",
                  }}
                >
                  <Box sx={{ paddingRight: "12px" }}>View JSON</Box>
                  <Box
                    sx={{
                      width: "1px",
                      height: "24px",
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                    }}
                  />
                  <Box
                    onClick={handleCopyPayload}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      paddingLeft: "12px",
                      cursor: "pointer",
                    }}
                  >
                    <ContentCopyIcon sx={{ fontSize: "18px" }} />
                  </Box>
                </Button>
              )}
              <Button
                type="submit"
                className="btn-orange"
                color="secondary"
                variant="contained"
                onClick={handleExecuteSubmit}
              >
                Re-Validate
              </Button>
            </FlexBetween>
          )}
        </Box>
      </div>
      <ViewPayloadDialog
        openDialog={isPayloadDialogOpen}
        payload={reRunValidationPayload}
        handleDialogClose={() => {
          setIsPayloadDialogOpen(false);
          setValidationResponse(null);
        }}
        dialogTitle="Resource Re-Run Validation JSON"
        onValidate={handleValidatePayload}
        isValidating={isValidating}
        validationResponse={validationResponse}
      />
    </>
  );
};

export default ReRunValidateResource;
